import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useMe } from '~/api/auth';
import { personQueryOptions } from '~/api/people';
import { reportsQueryOptions } from '~/api/reports';
import { AllReports } from '~/features/reports/AllReports';
import { ReportDays } from '~/features/reports/ReportDays';
import { ReportRequests } from '~/features/reports/ReportRequests';
import { ReportDrawer } from '~/features/reports/ReportDrawer';
import { TeamReports } from '~/features/reports/TeamReports';
import { Button } from '~/ui/Button';
import { PageHeader } from '~/ui/PageHeader';
import { Tabs } from '~/ui/Tabs';

type View = 'mine' | 'team' | 'requests' | 'all' | 'person';
const VIEWS: View[] = ['mine', 'team', 'requests', 'all', 'person'];

interface Search {
  view?: View;
  /** Open the caller's own report for this day (from a reminder or a row). */
  write?: string;
  /** Open the caller's own report for this day, to read it. */
  open?: string;
  /** The person shown in the `person` view. */
  person?: string;
  /** The day shown in the `team` view; absent = today. */
  date?: string;
}

const isDate = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

export const Route = createFileRoute('/_app/reports')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    view: VIEWS.includes(s['view'] as View) ? (s['view'] as View) : undefined,
    write: isDate(s['write']) ? s['write'] : undefined,
    open: isDate(s['open']) ? s['open'] : undefined,
    person: typeof s['person'] === 'string' ? s['person'] : undefined,
    date: isDate(s['date']) ? s['date'] : undefined,
  }),
  component: ReportsPage,
});

/** Someone else's day open in the sheet: to read it, approve it, or write it for them. */
interface Other {
  personId: string;
  date: string;
}

function ReportsPage() {
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/reports' });
  const lead = me.person.role !== 'member';
  const view: View =
    lead && search.view && (search.view !== 'person' || search.person) ? search.view : 'mine';
  const [other, setOther] = useState<Other | null>(null);
  const ownDate = search.write ?? search.open ?? null;

  const pending = useQuery({
    ...reportsQueryOptions({ state: 'pending', page: 1, pageSize: 1 }),
    enabled: lead,
  });

  const go = useCallback(
    (next: Partial<Search>) => void navigate({ search: (s) => ({ ...s, ...next }) }),
    [navigate],
  );
  const openOwn = useCallback((date: string) => go({ write: date, open: undefined }), [go]);
  const closeOwn = () => go({ write: undefined, open: undefined });
  const openOther = useCallback(
    (personId: string, date: string) =>
      personId === me.person.id ? openOwn(date) : setOther({ personId, date }),
    [me.person.id, openOwn],
  );

  return (
    <div className="mx-auto max-w-[1240px]">
      <PageHeader
        eyebrow="Work"
        title="Daily reports"
        subtitle="One report a day of what got done, open until midnight. It starts from your tasks and timer, so most of it is already written."
      />
      {lead && view !== 'person' && (
        <div className="mb-4" data-tour="reports-view">
          <Tabs
            label="Whose reports"
            value={view}
            onChange={(v) =>
              go({ view: v === 'mine' ? undefined : v, person: undefined, date: undefined })
            }
            options={[
              { value: 'mine', label: 'Mine' },
              { value: 'team', label: 'Team' },
              {
                value: 'requests',
                label: 'Approvals',
                count: pending.data?.total || undefined,
              },
              { value: 'all', label: 'All' },
            ]}
          />
        </div>
      )}

      {view === 'mine' && <ReportDays personId={null} onOpen={openOwn} />}
      {view === 'team' && (
        <TeamReports
          date={search.date}
          onDate={(date) => go({ date })}
          onWriteFor={(personId, date) => setOther({ personId, date })}
          onOpen={openOther}
          onPerson={(person) => go({ view: 'person', person, date: undefined })}
        />
      )}
      {view === 'requests' && <ReportRequests />}
      {view === 'all' && <AllReports onOpen={openOther} />}
      {view === 'person' && search.person && (
        <PersonReports
          personId={search.person}
          onBack={() => go({ view: 'team', person: undefined })}
          onOpen={(date) => openOther(search.person!, date)}
        />
      )}

      <ReportDrawer personId={null} date={ownDate} open={ownDate !== null} onClose={closeOwn} />
      <ReportDrawer
        personId={other?.personId ?? null}
        date={other?.date ?? null}
        open={other !== null}
        onClose={() => setOther(null)}
      />
    </div>
  );
}

function PersonReports({
  personId,
  onBack,
  onOpen,
}: {
  personId: string;
  onBack: () => void;
  onOpen: (date: string) => void;
}) {
  const person = useQuery(personQueryOptions(personId));
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="quiet"
          size="sm"
          icon={<ArrowLeft size={14} aria-hidden />}
          onClick={onBack}
        >
          Team
        </Button>
        <h2 className="text-lg">{person.data?.name ?? ' '}</h2>
      </div>
      <ReportDays personId={personId} onOpen={onOpen} />
    </div>
  );
}
